import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Grid2, Typography } from "@mui/material";
import { useStudents } from "../../../../../context/students";
import Preloader from "../../../../../components/Preloader";
import LockedPage from "../../../../../components/LockedPage";
import Breadcrumbs from "../../../../../components/Breadcrumbs";
import { useGetSessionNotes } from "../../../../../hooks/useGetSessionNotes";
import { useGetTerms } from "../../../../../hooks/useGetTerms";
import SessionNote from "../../../../../components/SessionNote";
import Select from "../../../../../components/Select";
import useStatuses from "../../../../../context/status/useStatuses";
import "./SessionNotes.css";

const SessionNotes = () => {
    const { t } = useTranslation();
    const { activeStudentId } = useStudents();
    const { showMentorPathway } = useStatuses();

    const { isLoading, sessionNotes, error } = useGetSessionNotes(activeStudentId, true);
    const { isLoading: isTermsLoading, terms } = useGetTerms(false);
    const [selectedTerm, setSelectedTerm] = useState("");

    const normalizeDateString = (value) => {
        if (typeof value !== "string") {
            return null;
        }

        const datePart = value.slice(0, 10);
        return /^\d{4}-\d{2}-\d{2}$/.test(datePart) ? datePart : null;
    };

    const getTermValue = (term) => `${term.name}|${term.start}|${term.end}`;
    const getTermYearLabel = (term) => {
        const startYear = normalizeDateString(term.start)?.slice(0, 4);
        const endYear = normalizeDateString(term.end)?.slice(0, 4);

        if (!startYear) {
            return "";
        }

        if (!endYear || startYear === endYear) {
            return startYear;
        }

        return `${startYear}/${endYear}`;
    };

    const isNoteInTerm = (note, term) => {
        const noteDate = normalizeDateString(note.sessionDate);
        const termStart = normalizeDateString(term.start);
        const termEnd = normalizeDateString(term.end);

        if (!noteDate || !termStart || !termEnd) {
            return false;
        }

        return noteDate >= termStart && noteDate <= termEnd;
    };

    const termsWithData = terms.filter((term) => sessionNotes.some((note) => isNoteInTerm(note, term)));

    const selectedTermDetails = terms.find((term) => getTermValue(term) === selectedTerm);
    const filteredSessionNotes = selectedTermDetails
        ? sessionNotes.filter((note) => isNoteInTerm(note, selectedTermDetails))
        : sessionNotes;

    return (
        <div className="page-content">
            <div className="student-head-section" />
            <Grid2 container spacing={2} className="min-head-section-height">
                <Grid2 size={{ xs: 12, md: 7, lg: 8 }}>
                    <Breadcrumbs
                        breadcrumbs={[
                            { label: t("navigation.home"), href: `/home/${activeStudentId}` },
                            { label: t("mentorPathway.title"), href: `/mentor-pathway/${activeStudentId}` },
                            { label: t("sessionNotes.title"), current: true },
                        ]}
                    />
                    <Typography variant="h1" className="text-white">
                        {t("sessionNotes.title")}
                    </Typography>
                    <Typography variant="body1" className="mt-4 text-white" component="div">
                        {t("sessionNotes.subtitle")}
                    </Typography>
                </Grid2>
                <Grid2 size={{ xs: 9, md: 5, lg: 4 }} />
            </Grid2>
            <Preloader isLoading={isLoading || isTermsLoading} error={error}>
                <LockedPage showPermissionText isLocked={!showMentorPathway}>
                    <div className="session-notes-select-container">
                        <Typography variant="caption">
                            {t(filteredSessionNotes.length > 1 ? "sessionNotes.reportsFound_plural" : "sessionNotes.reportsFound", { count: filteredSessionNotes.length })}
                        </Typography>
                        <Select
                            options={
                                [
                                    { label: "All Terms", value: "" },
                                    ...termsWithData.map((term) => (
                                        {
                                            label: `${term.name} (${getTermYearLabel(term)})`,
                                            value: getTermValue(term),
                                        }
                                    )),
                                ]
                            }
                            value={selectedTerm}
                            onChange={setSelectedTerm}
                            className="session-notes-select"
                        />
                    </div>
                    {filteredSessionNotes.map((sessionNote) => (
                        <SessionNote key={sessionNote.id} sessionNote={sessionNote} />
                    ))}
                </LockedPage>
            </Preloader>
        </div>
    );
};

export default SessionNotes;
